import { ThemeDefaults } from '@bbkFunnel/pages/theme';

export type ThemeLabelLocale =
  'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'ja' | 'ko';

export type ThemeLabelDefaults = Pick<
  typeof ThemeDefaults,
  | 'FORM_REQUIRED_LABEL'
  | 'FORM_REQUIRED_FIELD_ERROR'
  | 'SURVEY_ERROR_BOX_TITLE'
  | 'SURVEY_ERROR_BOX_CONTENT'
>;

const EN_LABEL_DEFAULTS: ThemeLabelDefaults = {
  FORM_REQUIRED_LABEL: ThemeDefaults.FORM_REQUIRED_LABEL,
  FORM_REQUIRED_FIELD_ERROR: ThemeDefaults.FORM_REQUIRED_FIELD_ERROR,
  SURVEY_ERROR_BOX_TITLE: ThemeDefaults.SURVEY_ERROR_BOX_TITLE,
  SURVEY_ERROR_BOX_CONTENT: ThemeDefaults.SURVEY_ERROR_BOX_CONTENT,
};

const ES_LABEL_DEFAULTS: ThemeLabelDefaults = {
  FORM_REQUIRED_LABEL: 'OBLIGATORIO',
  FORM_REQUIRED_FIELD_ERROR: 'Esta pregunta es obligatoria.',
  SURVEY_ERROR_BOX_TITLE: 'Se produjo un error.',
  SURVEY_ERROR_BOX_CONTENT: 'Complete todos los campos obligatorios.',
};

const FR_LABEL_DEFAULTS: ThemeLabelDefaults = {
  FORM_REQUIRED_LABEL: 'OBLIGATOIRE',
  FORM_REQUIRED_FIELD_ERROR: 'Cette question est obligatoire.',
  SURVEY_ERROR_BOX_TITLE: 'Une erreur est survenue.',
  SURVEY_ERROR_BOX_CONTENT: 'Veuillez remplir tous les champs obligatoires.',
};

const DE_LABEL_DEFAULTS: ThemeLabelDefaults = {
  FORM_REQUIRED_LABEL: 'ERFORDERLICH',
  FORM_REQUIRED_FIELD_ERROR: 'Diese Frage ist erforderlich.',
  SURVEY_ERROR_BOX_TITLE: 'Es ist ein Fehler aufgetreten.',
  SURVEY_ERROR_BOX_CONTENT: 'Bitte füllen Sie alle Pflichtfelder aus.',
};

const IT_LABEL_DEFAULTS: ThemeLabelDefaults = {
  FORM_REQUIRED_LABEL: 'OBBLIGATORIO',
  FORM_REQUIRED_FIELD_ERROR: 'Questa domanda è obbligatoria.',
  SURVEY_ERROR_BOX_TITLE: 'Si è verificato un errore.',
  SURVEY_ERROR_BOX_CONTENT: 'Compila tutti i campi obbligatori.',
};

const PT_LABEL_DEFAULTS: ThemeLabelDefaults = {
  FORM_REQUIRED_LABEL: 'OBRIGATÓRIO',
  FORM_REQUIRED_FIELD_ERROR: 'Esta pergunta é obrigatória.',
  SURVEY_ERROR_BOX_TITLE: 'Ocorreu um erro.',
  SURVEY_ERROR_BOX_CONTENT: 'Preencha todos os campos obrigatórios.',
};

const JA_LABEL_DEFAULTS: ThemeLabelDefaults = {
  FORM_REQUIRED_LABEL: '必須',
  FORM_REQUIRED_FIELD_ERROR: 'この質問は必須です。',
  SURVEY_ERROR_BOX_TITLE: 'エラーが発生しました。',
  SURVEY_ERROR_BOX_CONTENT: 'すべての必須項目を入力してください。',
};

const KO_LABEL_DEFAULTS: ThemeLabelDefaults = {
  FORM_REQUIRED_LABEL: '필수',
  FORM_REQUIRED_FIELD_ERROR: '이 질문은 필수입니다.',
  SURVEY_ERROR_BOX_TITLE: '오류가 발생했습니다.',
  SURVEY_ERROR_BOX_CONTENT: '모든 필수 항목을 입력해 주세요.',
};

const LABEL_DEFAULTS_BY_LOCALE: Record<ThemeLabelLocale, ThemeLabelDefaults> = {
  en: EN_LABEL_DEFAULTS,
  es: ES_LABEL_DEFAULTS,
  fr: FR_LABEL_DEFAULTS,
  de: DE_LABEL_DEFAULTS,
  it: IT_LABEL_DEFAULTS,
  pt: PT_LABEL_DEFAULTS,
  ja: JA_LABEL_DEFAULTS,
  ko: KO_LABEL_DEFAULTS,
};

// Explicit default labels for non-runtime callers (admin/editor/preview).
export const DEFAULT_THEME_LABEL_DEFAULTS: ThemeLabelDefaults =
  EN_LABEL_DEFAULTS;

const SUPPORTED_THEME_LABEL_LOCALES = Object.keys(
  LABEL_DEFAULTS_BY_LOCALE
) as ThemeLabelLocale[];

export const resolveThemeLabelLocale = (
  locale?: string | null
): ThemeLabelLocale => {
  if (!locale?.trim()) {
    return 'en';
  }

  // Match on the primary subtag, e.g. `pt-BR` -> `pt`.
  const primarySubtag = locale
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .split('-')[0];

  return SUPPORTED_THEME_LABEL_LOCALES.includes(
    primarySubtag as ThemeLabelLocale
  )
    ? (primarySubtag as ThemeLabelLocale)
    : 'en';
};

export const getThemeLabelDefaults = (
  locale?: string | null
): ThemeLabelDefaults =>
  LABEL_DEFAULTS_BY_LOCALE[resolveThemeLabelLocale(locale)];
